import { forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconBookmarkEmpty } from '@components/icons/src';
import { ConfirmModal } from '@components/modals';
import { ModalStickyFooter } from '@components/modals/modal-sticky-footer';
import { ModalStickyHeader } from '@components/modals/modal-sticky-header';
import { OptimizedImage } from '@components/optimized-image';
import { AssignOffersToFloorplanPreset } from '@components/presets/floorplan-preset-assign-admissions/assign-offers-to-floorplan-preset';
import { useAddAdmissionFloorplanPreset } from '@hooks/presets/floorplan/use-add-admission-floorplan-preset';
import { useDeleteFloorplanPreset } from '@hooks/presets/floorplan/use-delete-floorplan-preset';
import { useGetFloorplanPresetAdmissions } from '@hooks/presets/floorplan/use-get-floorplan-preset-admissions';

import { Props } from './publish-floorplan-preset.type';

export const PublishFloorplanPreset = forwardRef(
  ({ isEdit, floorplan, admissions, handleClose, handleSubmit }: Props, ref) => {
    const { t } = useTranslation();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [assignedOffers, setAssignedOffers] = useState<string[]>([]);
    const { mutate: deleteFloorplan } = useDeleteFloorplanPreset();
    const { mutate: assignAdmission } = useAddAdmissionFloorplanPreset(floorplan.id);
    const { data: floorplanAdmissions } = useGetFloorplanPresetAdmissions(floorplan.id);

    useImperativeHandle(ref, () => ({
      setIsDeleteModalOpen: () => {
        setIsDeleteModalOpen(true);
      },
    }));

    const handleSaveButton = () => {
      assignAdmission(assignedOffers);
      handleSubmit();
    };

    const handleDiscardButton = () => {
      if (!isEdit) deleteFloorplan({ floorplanId: floorplan.id });

      handleClose();
    };

    const handleCloseDeleteModal = () => {
      handleDiscardButton();
      setIsDeleteModalOpen(false);
    };

    const handleConfirmModalClick = () => {
      handleSaveButton();
      setIsDeleteModalOpen(false);
      handleClose();
    };

    return (
      <>
        <ModalStickyHeader
          title={isEdit ? t('floorplan:editFloorplanTitle') : t('floorplan:createNewFloorPlan')}
          handleClose={handleDiscardButton}
          bordered={true}
        />

        <div className='mx-7 mt-12'>
          <div className='flex justify-center'>
            <OptimizedImage
              className='object-contain max-h-152 rounded-t-md rounded-bl-md rounded-br-3xl'
              src={floorplan.imageUrl}
              alt='Cover event'
            />
          </div>
        </div>

        <AssignOffersToFloorplanPreset
          floorplan={floorplan}
          admissions={admissions}
          assignedOffers={assignedOffers}
          floorplanAdmissions={floorplanAdmissions}
          setAssignedOffers={setAssignedOffers}
        />

        <ModalStickyFooter
          primaryButtonText={t('common:doneButton')}
          onPrimaryButtonClick={handleSaveButton}
          onTertiaryButtonClick={() => {}}
          tertiaryButtonText={t('floorplan:saveFloorPlanAsPreset')}
          tertiaryIcon={
            <IconBookmarkEmpty className='mr-2 w-3 h-3 fill-current stroke-2 transition duration-200 ease-in-out' />
          }
        />

        <ConfirmModal
          isOpen={isDeleteModalOpen}
          title={t('floorplan:confirmModalFloorplanTitle')}
          handleClickConfirm={handleConfirmModalClick}
          handleClose={handleCloseDeleteModal}
        />
      </>
    );
  }
);
